import React from "react";
import { Alert, Box, Card, Grid, Icon, InputDateRange, InputText, Modal, Spinner, Table, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import StyledIcon from "~/components/StyledIcon";
import { AuthoritiesEnum, InsightsLineItemItemStatusEnum, InsightsLineItemItemTypeEnum } from "~/types/enums";
import useAbacExt from "~/hooks/abac";
import progressFlowActionCreators from "~/store/progressFlow/actions";
import { useDispatch, useSelector } from "react-redux";
import progressFlowSelectors from "~/store/progressFlow/selectors";
import styled, { useTheme } from "styled-components";
import { ProgressFlowActionEnum } from "~/store/progressFlow/types";
import InfiniteScroll from "react-infinite-scroll-component";
import InputTopicSelect from "../InputTopicSelect";
import ProgressFlowTableRow from "../insightsWidgets/ProgressFlowTableRow";
import InputUrgencySelect from "../InputUrgencySelect";
import ProgressFlowSelectionCard from "../ProgressFlowSelectionCard";
import ProgressFlowBulkDeleteModal from "../ProgressFlowBulkDeleteModal";
import ProgressFlowBulkMarkAsDoneModal from "../ProgressFlowBulkMarkAsDoneModal";
import ProgressFlowBulkDismissModal from "../ProgressFlowBulkDismissModal";
import ProgressFlowBulkMarkAsNotDoneModal from "../ProgressFlowBulkMarkAsNotDoneModal";
import InputProgressFlowTypeSelect from "../InputProgressFlowTypeSelect";
import InputProgressFlowStatusSelect from "../InputProgressFlowStatusSelect";
import ProgressFlowBulkMoveModal from "../ProgressFlowBulkMoveModal";
import ProgressFlowDetailsModal from "../ProgressFlowDetailsModal";
const StyledTable = styled(Table)`
  width: ${_ref => {
  let {
    theme
  } = _ref;
  return `calc(100% + ${10 * theme.typography.fontStep}rem)`;
}};
  height: 100%;
`;
const StyledTableHeader = styled(Table.Header)`
  border-bottom: 1px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};

  th {
    padding-bottom: 1em;
  }
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const ProgressFlowCard = _ref3 => {
  let {
    items,
    isEditable = false,
    preFilter,
    ...rest
  } = _ref3;
  const {
    t
  } = useTranslation();
  const {
    colors
  } = useTheme();
  const {
    userHas
  } = useAbacExt();
  const progressFlowItems = userHas(AuthoritiesEnum.EXPERIMENTAL) ? items : items.filter(item => !item.isPrivate);
  const dispatch = useDispatch();
  const filter = useSelector(progressFlowSelectors.getFilter);
  const error = useSelector(progressFlowSelectors.getError);
  const selectedData = useSelector(progressFlowSelectors.getSelectedData);
  const {
    hasAfter
  } = useSelector(progressFlowSelectors.getPagination);
  const selection = selectedData?.progressFlowItems || [];
  const action = useSelector(progressFlowSelectors.getAction);
  const handleFilter = (currFilter, newFilter) => {
    let updatedFilter = {
      ...currFilter,
      ...newFilter
    };
    if (newFilter.types?.includes(InsightsLineItemItemTypeEnum.ACHIEVEMENT)) {
      const existingStatuses = updatedFilter.statuses ?? [];
      updatedFilter = {
        ...updatedFilter,
        statuses: [...existingStatuses, InsightsLineItemItemStatusEnum.CLOSED]
      };
    }
    dispatch(progressFlowActionCreators.filtered({
      ...currFilter,
      ...updatedFilter
    }));
  };
  const handleToggleSelection = function (ids) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let sel = selection;
    if (!force) {
      const isAllSelected = ids.every(id => sel.includes(id));
      if (isAllSelected) {
        sel = sel.filter(id => !ids.includes(id));
      } else {
        const newValues = [...sel, ...ids];
        sel = newValues.filter((item, pos) => {
          return newValues.indexOf(item) === pos;
        });
      }
    } else {
      sel = ids;
    }
    dispatch(progressFlowActionCreators.actionChanged(null, null, {
      progressFlowItems: sel,
      count: sel.length
    }));
  };
  const handleLoadMore = () => {
    dispatch(progressFlowActionCreators.loadedMore());
  };
  return /*#__PURE__*/React.createElement(Grid, {
    cols: 1
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, {
    align: "right",
    s: {
      p: 0
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(InputDateRange, {
    initialStartDate: filter?.dateFrom,
    initialEndDate: filter.dateTo,
    onChange: (start, end) => handleFilter(filter, {
      dateFrom: start,
      dateTo: end
    }),
    showReset: true,
    fullWidth: true,
    maxWidth: "300px"
  })), /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 3,
    template: "1fr 180px 180px 180px 180px",
    s: {
      my: 4
    }
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("ProgressFlowCard.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    onChange: value => handleFilter(filter, {
      query: value
    }),
    color: "translucent",
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputProgressFlowTypeSelect, {
    color: "translucent",
    direction: "bottomLeft",
    value: filter?.types,
    onChange: value => handleFilter(filter, {
      types: value
    }),
    placeholder: t("ProgressFlowCard.allTypes"),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputProgressFlowStatusSelect, {
    color: "translucent",
    direction: "bottomLeft",
    value: filter?.statuses,
    onChange: value => handleFilter(filter, {
      statuses: value
    }),
    placeholder: t("ProgressFlowCard.allStatuses"),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputTopicSelect, {
    color: "translucent",
    direction: "bottomLeft",
    value: filter?.topicIds,
    onChange: value => handleFilter(filter, {
      topicIds: value
    }),
    placeholder: t("ProgressFlowCard.allTopics"),
    multiple: true,
    selectAll: true,
    noTopic: true
  }), /*#__PURE__*/React.createElement(InputUrgencySelect, {
    color: "translucent",
    direction: "bottomLeft",
    value: filter?.urgencies,
    onChange: value => handleFilter(filter, {
      urgencies: value
    }),
    placeholder: t("ProgressFlowCard.allUrgencies"),
    multiple: true
  })), /*#__PURE__*/React.createElement(Card, rest, progressFlowItems.length ? /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: progressFlowItems.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      center: true
    })
  }, /*#__PURE__*/React.createElement(StyledTable, {
    size: "m",
    fontSize: 3.2,
    tableProps: {
      style: {
        minWidth: "1000px"
      }
    },
    s: {
      mx: -5,
      mb: -5
    }
  }, /*#__PURE__*/React.createElement(StyledTableHeader, null, /*#__PURE__*/React.createElement(Table.Tr, null, userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    width: "180px"
  }, t("ProgressFlowCard.topic")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "250px"
  }, t("ProgressFlowCard.context")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "250px"
  }, t("ProgressFlowCard.action")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "150px"
  }, t("ProgressFlowCard.owner")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "120px"
  }, t("ProgressFlowCard.urgency")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "150px"
  }, t("ProgressFlowCard.observedAt")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "150px"
  }, t("ProgressFlowCard.statusClosedDate")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "0"
  }))), /*#__PURE__*/React.createElement(Table.Body, null, progressFlowItems.map(item => /*#__PURE__*/React.createElement(ProgressFlowTableRow, {
    key: item.id,
    item: item,
    selection: selection,
    setSelection: handleToggleSelection
  }))))) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    justify: "center",
    align: "center",
    fullHeight: true
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "tick",
    color: "translucentSuccess"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    },
    align: "center",
    style: {
      maxWidth: "300px"
    }
  }, t("ProgressFlowCard.noBlockersOrActionItemsFound")))), !!selection.length && /*#__PURE__*/React.createElement(ProgressFlowSelectionCard, {
    selection: selection,
    setSelection: handleToggleSelection
  }), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.BULK_DELETE
  }, /*#__PURE__*/React.createElement(ProgressFlowBulkDeleteModal, null)), userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.BULK_MARK_AS_DONE
  }, /*#__PURE__*/React.createElement(ProgressFlowBulkMarkAsDoneModal, null)), userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.BULK_MARK_AS_NOT_DONE
  }, /*#__PURE__*/React.createElement(ProgressFlowBulkMarkAsNotDoneModal, null)), userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.BULK_DISMISS
  }, /*#__PURE__*/React.createElement(ProgressFlowBulkDismissModal, null)), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.BULK_MOVE
  }, /*#__PURE__*/React.createElement(ProgressFlowBulkMoveModal, null)), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Modal, {
    open: action === ProgressFlowActionEnum.DETAILS
  }, /*#__PURE__*/React.createElement(ProgressFlowDetailsModal, null)));
};
export default ProgressFlowCard;